import { useEffect, useState } from 'react'

/**
 * THE KEYBOARD INSET.
 *
 * The problem this exists for: there are two viewports, and the software keyboard
 * only shrinks one of them. `100dvh` tracks browser chrome but NOT the keyboard,
 * so when it opens the layout still claims a screen that is no longer there — and
 * the browser compensates by SCROLLING the focused input into view. That scroll
 * is the jank: the feed slides up on focus and back down on dismiss, and nothing
 * in our layout asked for either.
 *
 * The fix is to stop claiming the space. `visualViewport.height` is the real
 * room above the keyboard; a surface sized off it shrinks instead of shifting,
 * and a bottom-anchored transcript keeps its last lines exactly where they were.
 *
 * Paired with `interactive-widget=resizes-content` in index.html, which does the
 * same job declaratively where it is supported (Chrome/Android). Safari does not
 * support it, which is why this hook is the one that actually matters.
 *
 * Returns 0 on desktop and whenever no keyboard is up, so callers can subtract it
 * unconditionally.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const read = () => {
      /*
       * The gap between the layout viewport and the visual one. `offsetTop` is
       * included because iOS shifts the visual viewport upward rather than only
       * shrinking it, and ignoring that leaves the composer under the keyboard by
       * exactly the offset.
       *
       * Rounded and floored at zero: sub-pixel noise during the open animation
       * would otherwise write a new CSS value on every frame, and a negative
       * inset (briefly possible mid-animation) would grow the shell past the
       * screen.
       */
      const next = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      // Under this, it is browser chrome settling rather than a keyboard. Treating
      // small changes as a keyboard makes the surface twitch while merely scrolling.
      setInset(next > 120 ? next : 0)
    }

    read()
    vv.addEventListener('resize', read)
    vv.addEventListener('scroll', read)
    return () => {
      vv.removeEventListener('resize', read)
      vv.removeEventListener('scroll', read)
    }
  }, [])

  return inset
}
